import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { QuickPlayService } from '../../services/quick-play.service';

export interface GenreSelectionData {
  selectedGenres: string[];
}

@Component({
  selector: 'app-genre-selection',
  templateUrl: './genre-selection.component.html',
  styleUrls: ['./genre-selection.component.scss']
})
export class GenreSelectionComponent implements OnInit {
  availableGenres: string[] = [];
  selectedGenres: Set<string> = new Set();
  loading = true;
  error: string | null = null;

  // Grouped genres for better organization
  genreGroups: { [key: string]: string[] } = {
    'Popular': ['pop', 'rock', 'hip-hop', 'r-n-b', 'indie'],
    'Electronic': ['electronic', 'edm', 'house', 'techno', 'trance', 'dubstep', 'drum-and-bass'],
    'Chill': ['chill', 'ambient', 'new-age', 'study'],
    'Party': ['dance', 'party', 'disco', 'club'],
    'Classic': ['jazz', 'classical', 'blues', 'soul', 'funk'],
    'Alternative': ['alternative', 'grunge', 'punk', 'metal', 'garage'],
    'World': ['latin', 'reggae', 'afrobeat', 'world-music', 'k-pop', 'spanish'],
    'Country & Folk': ['country', 'folk', 'americana', 'bluegrass']
  };

  constructor(
    public dialogRef: MatDialogRef<GenreSelectionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GenreSelectionData,
    private quickPlayService: QuickPlayService
  ) {
    this.selectedGenres = new Set(data.selectedGenres || []);
  }

  ngOnInit(): void {
    this.loadGenres();
  }

  private loadGenres(): void {
    this.quickPlayService.getAvailableGenres().subscribe({
      next: (response) => {
        // Ensure we have an array of genres
        this.availableGenres = Array.isArray(response.genres) ? response.genres : 
                               Array.isArray(response) ? response : 
                               Object.keys(response) || [];
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading genres:', error);
        this.error = 'Failed to load genres';
        this.loading = false;
      }
    });
  }

  toggleGenre(genre: string): void {
    if (this.selectedGenres.has(genre)) {
      this.selectedGenres.delete(genre);
    } else {
      this.selectedGenres.add(genre);
    }
  }

  isGenreSelected(genre: string): boolean {
    return this.selectedGenres.has(genre);
  }

  getGenresByGroup(groupName: string): string[] {
    // Return all genres in the group regardless of API availability
    // Users should be able to select any predefined genre
    return this.genreGroups[groupName] || [];
  }

  selectAll(): void {
    // Select all predefined genres from all groups
    Object.values(this.genreGroups).flat().forEach(genre => this.selectedGenres.add(genre));
  }

  clearAll(): void {
    this.selectedGenres.clear();
  }

  selectGroup(groupName: string): void {
    const groupGenres = this.getGenresByGroup(groupName);
    groupGenres.forEach(genre => this.selectedGenres.add(genre));
  }

  onSave(): void {
    this.dialogRef.close({
      genres: Array.from(this.selectedGenres)
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getGenreDisplayName(genre: string): string {
    return genre.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  getSelectedCount(): number {
    return this.selectedGenres.size;
  }

  getSelectedGenresArray(): string[] {
    return Array.from(this.selectedGenres);
  }

  removeGenre(genre: string): void {
    this.selectedGenres.delete(genre);
  }

  getTotalGenreCount(): number {
    return Object.values(this.genreGroups).flat().length;
  }
}